// MODIFIED: Updated relative paths for common components
import { useState, useEffect, useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { Input, TextArea } from "../../../../../common/Input";
import { StatusBadge } from "../../../../../common/Badge";

export function FinancialRequestDetailsModal({ isOpen, onClose, request }) {
  const [activeTab, setActiveTab] = useState("details");
  useEffect(() => { if (isOpen) setActiveTab("details"); }, [isOpen]);

  // // MODIFIED: Helper function to group any trail array by its hierarchy property
  const groupTrailByLevel = (trailArray) => {
    if (!trailArray || trailArray.length === 0) return {};
    return trailArray.reduce((acc, app) => {
      const lvl = app.hierarchy;
      if (!acc[lvl]) acc[lvl] = [];
      acc[lvl].push(app);
      return acc;
    }, {});
  };

  // // MODIFIED: Grouping logic now handles the split trail structure ──────────────
  const groupedHierarchy = useMemo(() => groupTrailByLevel(request?.hierarchyTrail), [request]);
  const groupedAmount = useMemo(() => groupTrailByLevel(request?.amountTrail), [request]);

  if (!isOpen || !request) return null;

  const tabs = [
    { key: "details", label: "Request Info", icon: <DescriptionIcon fontSize="small" /> },
    { key: "trail", label: "Approval Trail", icon: <AssignmentTurnedInIcon fontSize="small" /> },
  ];

  // // ADDED: Shared component for rendering a group of approver slots ──────────────
  const RenderTrailGroup = ({ groupedData, titlePrefix }) => {
    return Object.keys(groupedData).sort().map((lvl) => (
      <div key={lvl} className="border border-gray-300 rounded-lg shadow-sm overflow-hidden mb-4">
        <div className="px-4 py-2 bg-gray-100 border-b flex items-center gap-2">
          <span className="w-6 h-6 bg-blue-500 text-white rounded-full text-xs flex items-center justify-center font-bold">{lvl}</span>
          <h3 className="font-bold text-gray-700 text-sm">{titlePrefix} {lvl}</h3>
        </div>
        <div className="p-4 space-y-3 bg-white">
          {groupedData[lvl].map((app, idx) => (
            <div key={idx} className="flex items-start gap-3">
              <div className="flex-1 bg-gray-50 rounded-lg border border-gray-200 p-3">
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-bold text-blue-900">{app.approverFullName}</span>
                  <StatusBadge status={app.status} />
                </div>
                {app.status !== "Pending" && (
                  <div className="bg-white p-2 border border-gray-100 text-xs italic text-gray-600 rounded">
                    "{app.remarks || 'No remarks'}"
                  </div>
                )}
                <div className="text-right text-[9px] text-gray-400 font-bold mt-2 uppercase">
                  {app.actionDate ? new Date(app.actionDate).toLocaleString() : app.status === "Pending" ? 'AWAITING ACTION' : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    ));
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 overflow-auto">
      <div className="bg-white w-full md:w-[80%] lg:w-[60%] xl:w-[50%] rounded-lg shadow-2xl flex flex-col h-[85vh]">
        <div className="flex justify-between items-center px-6 py-3 border-b bg-gray-50 rounded-t-lg">
          <h2 className="text-lg font-bold text-blue-900 flex items-center gap-2 uppercase">
            <AccountBalanceWalletIcon className="text-blue-700" />
            Request Details
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-red-500 bg-transparent border-none cursor-pointer"><CloseIcon /></button>
        </div>

        <div className="border-b border-gray-200 bg-white pt-3">
          <div className="flex space-x-1 px-6 overflow-x-auto">
            {tabs.map((tab) => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`px-6 py-3 text-sm font-bold rounded-t-lg transition-colors border-b-2 flex items-center gap-2 border-none cursor-pointer whitespace-nowrap ${activeTab === tab.key ? "border-b-2 border-blue-500 text-blue-600 bg-blue-50" : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"}`}>
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-grow bg-white">
          {activeTab === "details" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
              <Input label="Ref No." value={request.referenceNumber} isViewOnly />
              <Input label="Employee" value={request.employeeFullName} isViewOnly />
              <Input label="Amount" value={`₱${request.amount?.toLocaleString()}`} isViewOnly />
              <div className="flex flex-col">
                <label className="mb-1.5 text-sm font-medium text-gray-600 uppercase text-[11px]">Status</label>
                <div className="pt-1"><StatusBadge status={request.status} /></div>
              </div>
              <div className="md:col-span-2"><TextArea label="Reason" value={request.reason} isViewOnly rows={4} /></div>
            </div>
          ) : (
            <div className="space-y-6">

              {/* // ADDED: PHASE 1 SECTION ────────────────────────────── */}
              <div>
                <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <div className="h-[1px] bg-gray-200 flex-grow"></div>
                  Phase 1: Standard Hierarchy
                  <div className="h-[1px] bg-gray-200 flex-grow"></div>
                </h4>
                <RenderTrailGroup groupedData={groupedHierarchy} titlePrefix="Level" />
              </div>

              {/* // ADDED: PHASE 2 SECTION (Conditional) ───────────────── */}
              {Object.keys(groupedAmount).length > 0 && (
                <div className="mt-8">
                  <h4 className="text-[10px] font-bold text-blue-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                    <div className="h-[1px] bg-blue-100 flex-grow"></div>
                    Phase 2: Amount-Based Authorization
                    <div className="h-[1px] bg-blue-100 flex-grow"></div>
                  </h4>
                  <RenderTrailGroup groupedData={groupedAmount} titlePrefix="Rule Group" />
                </div>
              )}

              {/* Status context for Pending requests */}
              {request.status === "Pending" && (
                <div className="p-4 bg-amber-50 border border-dashed border-amber-300 rounded-lg flex items-center gap-3">
                  <div className="animate-pulse w-2 h-2 rounded-full bg-amber-500"></div>
                  <p className="text-xs font-bold text-amber-700 italic">
                    {request.approvalStrategy === "Standard"
                      ? `Waiting for Level ${request.currentHierarchy} consensus...`
                      : `Awaiting Phase 2 Amount-Based verification...`}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-gray-50 flex justify-between items-center rounded-b-lg">
          <span className="text-xs text-gray-400 font-bold uppercase px-2">Ref: {request.referenceNumber}</span>
          <button onClick={onClose} className="px-8 py-2 bg-gray-400 text-white rounded font-bold cursor-pointer border-none text-sm hover:bg-gray-500">Close</button>
        </div>
      </div>
    </div>
  );
}